import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import { formatCurrency, formatDate } from "@/lib/utils"
import { taxEstimatePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface Quarter {
  quarter: string
  start_date: string
  end_date: string
  revenue: number
  expenses: number
  net_profit: number
  federal_estimate: number
  state_estimate: number
  total_estimate: number
  taxes_paid: number
  deadline: string
}

interface TaxEstimateProps {
  year: number
  federal_rate: number
  state_rate: number
  revenue: number
  expenses: number
  net_profit: number
  annual_federal: number
  annual_state: number
  annual_total: number
  taxes_paid: number
  remaining: number
  overpaid: number
  quarters: Quarter[]
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Tax Estimates", href: taxEstimatePath() },
]

function currentQuarter(): number {
  return Math.ceil((new Date().getMonth() + 1) / 3)
}

export default function TaxEstimate({
  year,
  federal_rate,
  state_rate,
  annual_federal,
  annual_state,
  annual_total,
  taxes_paid,
  remaining,
  overpaid,
  quarters,
}: TaxEstimateProps) {
  const [fedRate, setFedRate] = useState(federal_rate.toString())
  const [stRate, setStRate] = useState(state_rate.toString())

  function applyRates() {
    const fed = parseFloat(fedRate)
    const st = parseFloat(stRate)
    if (!isNaN(fed) && !isNaN(st) && fed >= 0 && st >= 0) {
      router.get(
        taxEstimatePath(),
        { year, federal_rate: fed, state_rate: st },
        { preserveState: true },
      )
    }
  }

  function changeYear(offset: number) {
    router.get(
      taxEstimatePath(),
      { year: year + offset, federal_rate, state_rate },
      { preserveState: true },
    )
  }

  const cq = currentQuarter()

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Tax Estimates" />

      <div className="mx-auto w-full max-w-3xl space-y-6 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Tax Estimates</h2>
            <p className="text-muted-foreground text-sm">
              Estimated quarterly tax payments for {year}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => changeYear(-1)}>
              {year - 1}
            </Button>
            <span className="font-mono text-sm font-medium">{year}</span>
            <Button variant="outline" size="sm" onClick={() => changeYear(1)}>
              {year + 1}
            </Button>
          </div>
        </div>

        <div className="flex items-end gap-3">
          <div className="grid gap-1">
            <Label className="text-xs">Federal Rate (%)</Label>
            <Input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={fedRate}
              onChange={(e) => setFedRate(e.target.value)}
              className="w-24"
            />
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">State Rate (%)</Label>
            <Input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={stRate}
              onChange={(e) => setStRate(e.target.value)}
              className="w-24"
            />
          </div>
          <Button size="sm" onClick={applyRates}>
            Update
          </Button>
          <span className="text-muted-foreground mb-2 text-xs">
            Combined: {federal_rate + state_rate}%
          </span>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                Annual Tax Estimate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">
                {formatCurrency(annual_total)}
              </div>
              <p className="text-muted-foreground text-xs">
                Federal {formatCurrency(annual_federal)} + State{" "}
                {formatCurrency(annual_state)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Taxes Paid</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(taxes_paid)}
              </div>
              <p className="text-muted-foreground text-xs">
                YTD payments recorded
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                {overpaid > 0 ? "Overpaid" : "Remaining"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className={`text-2xl font-bold ${overpaid > 0 ? "text-green-600" : "text-red-600"}`}
              >
                {formatCurrency(overpaid > 0 ? overpaid : remaining)}
              </div>
              <p className="text-muted-foreground text-xs">
                {overpaid > 0
                  ? "Credit toward next quarter"
                  : "Still owed this year"}
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">
              Quarterly Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {quarters.map((q) => {
                const qNum = parseInt(q.quarter.slice(1))
                const isPast =
                  year < new Date().getFullYear() ||
                  (year === new Date().getFullYear() && qNum < cq)
                const isCurrent =
                  year === new Date().getFullYear() && qNum === cq
                const balance = q.total_estimate - q.taxes_paid

                return (
                  <div
                    key={q.quarter}
                    className={`rounded-lg border p-4 ${isCurrent ? "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {q.quarter} {year}
                        </span>
                        {isCurrent && (
                          <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                            Current
                          </Badge>
                        )}
                        <span className="text-muted-foreground text-xs">
                          {formatDate(q.start_date)} - {formatDate(q.end_date)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium">
                          Due: {q.deadline}
                        </span>
                        {q.taxes_paid > 0 && balance <= 0 ? (
                          <Badge variant="outline" className="text-green-600">
                            Paid
                          </Badge>
                        ) : q.total_estimate === 0 ? (
                          <span className="text-muted-foreground text-xs">
                            No income
                          </span>
                        ) : isPast && q.taxes_paid === 0 ? (
                          <Badge variant="outline" className="text-red-600">
                            Unpaid
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:grid-cols-5">
                      <div>
                        <p className="text-muted-foreground text-xs">
                          Net Profit
                        </p>
                        <p className="font-mono font-medium">
                          {formatCurrency(q.net_profit)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Federal</p>
                        <p className="font-mono font-medium text-amber-600">
                          {formatCurrency(q.federal_estimate)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">State</p>
                        <p className="font-mono font-medium text-amber-600">
                          {formatCurrency(q.state_estimate)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Paid</p>
                        <p className="font-mono font-medium text-green-600">
                          {formatCurrency(q.taxes_paid)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Balance</p>
                        <p
                          className={`font-mono font-medium ${balance <= 0 ? "text-green-600" : "text-red-600"}`}
                        >
                          {balance <= 0
                            ? formatCurrency(0)
                            : formatCurrency(balance)}
                        </p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
